// frontend/src/pages/admin/EditResearchPage.tsx
import { useState, useEffect, type FormEvent, type ChangeEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { ArrowLeft, Save, Loader2, AlertCircle, CheckCircle, UploadCloud, FileText } from 'lucide-react';
import { getResearchItemById, updateResearchItem } from '@/api/researchService';
import { getAllTags } from '@/api/tagService';
import { type ResearchUpdateJsonPayload, type TagPublic } from '@/types';

const EditResearchPage = () => {
  const navigate = useNavigate();
  const { researchId } = useParams<{ researchId: string }>();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState<'draft' | 'published' | 'archived'>('draft');
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);
  const [currentPdfUrl, setCurrentPdfUrl] = useState<string | null | undefined>(null);
  const [newPdfFile, setNewPdfFile] = useState<File | null>(null);
  const [newPdfFileName, setNewPdfFileName] = useState<string>('');

  const [allTags, setAllTags] = useState<TagPublic[]>([]);
  const [isLoading, setIsLoading] = useState(true); // For initial data load
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const backendBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';

  useEffect(() => {
    if (!researchId) {
      setError("Research ID is missing.");
      setIsLoading(false);
      return;
    }

    const fetchResearchItemAndTags = async () => {
      setIsLoading(true);
      try {
        const [itemData, tagsData] = await Promise.all([
          getResearchItemById(parseInt(researchId, 10)),
          getAllTags()
        ]);

        setTitle(itemData.title);
        setDescription(itemData.description || '');
        setStatus(itemData.status);
        setSelectedTagIds(itemData.tags.map(tag => tag.id));
        setCurrentPdfUrl(itemData.pdf_url);
        setAllTags(tagsData);
        setError(null);
      } catch (err) {
        console.error("Failed to fetch research item or tags:", err);
        setError("Failed to load research data. Please try again.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchResearchItemAndTags();
  }, [researchId]);

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (file.type === "application/pdf") {
        setNewPdfFile(file);
        setNewPdfFileName(file.name);
        setError(null); // Clear previous file errors
      } else {
        setNewPdfFile(null);
        setNewPdfFileName('');
        setError("Invalid file type. Please upload a PDF.");
      }
    } else {
      setNewPdfFile(null);
      setNewPdfFileName('');
    }
  };

  const handleTagSelection = (tagIdString: string) => {
    const id = parseInt(tagIdString, 10);
    setSelectedTagIds(prev =>
      prev.includes(id) ? prev.filter(tId => tId !== id) : [...prev, id]
    );
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!researchId) {
      setError("Research ID is missing for update.");
      return;
    }
    setIsSubmitting(true);
    setError(null);
    setSuccessMessage(null);

    if (!title) {
      setError("Title is required.");
      setIsSubmitting(false);
      return;
    }

    const jsonDataPayload: ResearchUpdateJsonPayload = {
      title,
      description: description || null,
      status,
      tag_ids: selectedTagIds.length > 0 ? selectedTagIds : null,
    };

    try {
      const updatedResearchItem = await updateResearchItem(parseInt(researchId, 10), jsonDataPayload, newPdfFile);
      setSuccessMessage(`Research item "${updatedResearchItem.title}" updated successfully! Redirecting...`);
      setCurrentPdfUrl(updatedResearchItem.pdf_url); // Update current PDF URL if changed
      setNewPdfFile(null); // Clear the new PDF file input
      setNewPdfFileName('');
      setTimeout(() => {
        navigate('/admin/research');
      }, 2000);
    } catch (err: any) {
      console.error("Failed to update research item:", err);
      if (err.response && err.response.data && err.response.data.detail) {
         setError(typeof err.response.data.detail === 'string' ? err.response.data.detail : JSON.stringify(err.response.data.detail));
      } else if (err.request) {
        setError("Network error or no response from server.");
      }
      else {
        setError("Failed to update research item. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-20 flex flex-col items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
        <p className="text-lg text-muted-foreground">Loading research data...</p>
      </div>
    );
  }

  if (error && !title) { // Show critical error if item couldn't be loaded
    return (
      <div className="container mx-auto px-4 py-20 text-center">
        <AlertCircle className="h-16 w-16 text-destructive mx-auto mb-6" />
        <h2 className="text-2xl font-semibold text-destructive mb-2">Error Loading Research Item</h2>
        <p className="text-muted-foreground mb-6">{error}</p>
        <Button variant="outline" onClick={() => navigate(-1)}>
          <ArrowLeft className="mr-2 h-4 w-4" /> Go Back
        </Button>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 md:py-12">
      <div className="mb-6">
        <Button variant="outline" onClick={() => navigate("/admin/research")} className="text-primary hover:text-primary/80 cursor-pointer">
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to Manage Research
        </Button>
      </div>

      <Card className="max-w-3xl mx-auto bg-card border-border/30 shadow-lg">
        <CardHeader>
          <CardTitle className="text-2xl md:text-3xl text-foreground">Edit Research Item</CardTitle>
          <CardDescription className="text-muted-foreground">Modify the details and PDF for the research item.</CardDescription>
        </CardHeader>
        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-6">
            {error && ( // For submission errors
              <div className="p-3 bg-destructive/10 text-destructive border border-destructive/30 rounded-md flex items-center">
                <AlertCircle className="h-5 w-5 mr-2" /> {error}
              </div>
            )}
            {successMessage && (
              <div className="p-3 bg-green-500/10 text-green-700 dark:text-green-400 border border-green-500/30 rounded-md flex items-center">
                <CheckCircle className="h-5 w-5 mr-2" /> {successMessage}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="title" className="text-foreground">Title <span className="text-destructive">*</span></Label>
              <Input
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter research title"
                required
                className="!bg-input border-border focus:border-primary focus:ring-primary"
                disabled={isSubmitting}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description" className="text-foreground">Description (Optional)</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Brief summary of the research paper..."
                rows={4}
                className="!bg-input border-border focus:border-primary focus:ring-primary min-h-[100px]"
                disabled={isSubmitting}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="pdfFile" className="text-foreground">PDF Document</Label>
              {currentPdfUrl && (
                <div className="mb-2 text-sm text-muted-foreground">
                  Current file: <a href={`${backendBaseUrl}/static/${currentPdfUrl}`} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">{currentPdfUrl.split('/').pop()} <FileText className="inline h-4 w-4 ml-1"/></a>
                </div>
              )}
              <div className="flex items-center space-x-3">
                <Input
                  id="pdfFile"
                  type="file"
                  accept=".pdf"
                  onChange={handleFileChange}
                  className="!bg-input border-border focus:border-primary focus:ring-primary file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-primary/10 file:text-primary hover:file:bg-primary/20 cursor-pointer"
                  disabled={isSubmitting}
                />
                 <UploadCloud className="h-6 w-6 text-muted-foreground" />
              </div>
              {newPdfFileName && <p className="text-sm text-muted-foreground mt-1">New file selected: {newPdfFileName} (will replace current if saved)</p>}
              {!newPdfFileName && <p className="text-xs text-muted-foreground mt-1">To replace the current PDF, upload a new one.</p>}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="status" className="text-foreground">Status</Label>
                <Select
                  value={status}
                  onValueChange={(value: 'draft' | 'published' | 'archived') => setStatus(value)}
                  disabled={isSubmitting}
                >
                  <SelectTrigger className="!bg-input border-border focus:border-primary focus:ring-primary cursor-pointer">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent className="bg-popover border-border">
                    <SelectItem value="draft" className='cursor-pointer'>Draft</SelectItem>
                    <SelectItem value="published" className='cursor-pointer'>Published</SelectItem>
                    <SelectItem value="archived" className='cursor-pointer'>Archived</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2 pb-4">
                <Label className="text-foreground">Tags (Optional)</Label>
                <div className="flex flex-wrap gap-2 p-3 border border-input rounded-md min-h-[40px] bg-background">
                    {allTags.length > 0 ? allTags.map(tag => (
                        <Button
                            type="button" // Important for forms with multiple buttons
                            key={tag.id}
                            variant={selectedTagIds.includes(tag.id) ? "default" : "outline"}
                            size="sm"
                            onClick={() => handleTagSelection(String(tag.id))}
                            className={`transition-all cursor-pointer ${selectedTagIds.includes(tag.id) ? '!bg-primary text-primary-foreground' : 'border-border text-muted-foreground hover:bg-accent/10'}`}
                            disabled={isSubmitting}
                        >
                            {tag.name}
                        </Button>
                    )) : <p className="text-sm text-muted-foreground">No tags available or loading...</p>}
                </div>
            </div>

          </CardContent>
          <CardFooter className="border-t border-border/30 pt-6">
            <Button type="submit" className="w-full md:w-auto !bg-gradient-to-r !from-primary !to-accent hover:!from-primary/90 hover:!to-accent/90 text-primary-foreground cursor-pointer" disabled={isSubmitting || isLoading}>
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Updating Research...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" /> Save Changes
                </>
              )}
            </Button>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
};

export default EditResearchPage;